import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Loader2, Truck } from "lucide-react";
import { api } from "../lib/config";
import { toast } from "sonner";

const QUICK = [5, 10, 15, 30, 45, 60];

export default function OnMyWayDialog({ open, onOpenChange, booking, onSent }) {
  const [etaMinutes, setEtaMinutes] = useState(15);
  const [customMessage, setCustomMessage] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post(`/admin/bookings/${booking.booking_id}/on-my-way`, {
        eta_minutes: etaMinutes,
        custom_message: customMessage || null,
      });
      toast.success(`Customer notified — ETA ${etaMinutes} min`);
      onSent && onSent(data.booking);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setSubmitting(false); }
  };

  if (!booking) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="on-my-way-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><Truck className="w-5 h-5 text-[#047857]" /> On The Way · {booking.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-1">
          <div className="text-xs text-gray-500">{booking.service_name} · {booking.address || "—"}</div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-600 mb-2">ETA</div>
            <div className="grid grid-cols-6 gap-1.5" data-testid="omw-quick-eta">
              {QUICK.map((m) => (
                <button key={m} type="button" onClick={() => setEtaMinutes(m)}
                  data-testid={`omw-eta-${m}`}
                  className={`h-10 rounded-lg border text-sm font-extrabold ${etaMinutes === m ? "bg-[#062818] text-white border-transparent" : "bg-white border-gray-200"}`}>
                  {m}m
                </button>
              ))}
            </div>
            <div className="mt-2 flex items-center gap-2">
              <input type="number" min={1} max={240} value={etaMinutes} onChange={(e) => setEtaMinutes(Math.max(1, Math.min(240, parseInt(e.target.value) || 1)))}
                data-testid="omw-eta-input"
                className="w-24 h-10 rounded-lg border border-gray-200 px-3 text-base font-extrabold tabular-nums" />
              <span className="text-xs text-gray-500">minutes</span>
            </div>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-[0.18em] font-extrabold text-gray-600 mb-1.5">Custom message (optional)</div>
            <Textarea rows={2} value={customMessage} onChange={(e) => setCustomMessage(e.target.value)}
              placeholder="Override the default 'on the way · ETA' message"
              data-testid="omw-custom-message" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={submitting} data-testid="omw-send-btn" className="rounded-full bg-[#047857] hover:bg-[#065F46] text-white font-extrabold">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Send ETA"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
